import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { useAuth0 } from './react-auth0-spa'
import xhr from 'xhr'
import { makeStyles } from '@material-ui/core'

import App from './App'
import config from './config'
import Loader from './common/components/notifications/Loader'
import createUppy from './utils/CreateUppy'
import actions from './redux/actions'

const useStyles = makeStyles(() => ({
    loaderMessage: { 
        display: 'flex',
        justifyContent: 'center',
        paddingtop: '5%'
    }
}))

/**
 * Namespaced Auth0 custom claim carrying the Invintus `clientID`.
 *
 * Auth0 only attaches it for a user provisioned against the Auth0
 * APPLICATION that issued the token. A token minted by a different
 * application -- the Enterprise app vs the standard one, or a session
 * left over from a different origin such as production while you are on
 * localhost -- authenticates fine and simply arrives WITHOUT this claim.
 */
const USER_METADATA_CLAIM = 'http://invintus_user_metadata'

/**
 * clientID from the Auth0 user, or undefined when the claim is absent.
 *
 * Read through this rather than indexing the claim directly. The claim
 * is a nested object, so `user[CLAIM].clientID` throws a TypeError the
 * moment the claim is missing -- and it goes missing for an ordinary,
 * recoverable reason (a stale cross-origin session), which used to take
 * the whole app down with an unhandled error instead of showing the user
 * anything they could act on.
 */
const getUserClientID = user => {
    const metadata = user && user[USER_METADATA_CLAIM]
    return metadata ? metadata.clientID : undefined
}

/**
 * convert '0' strings to true/ false values
 * @param {Object} obj the objed to get the correct key values for
 * @summary given an object with keys like `'isTrue':'1'` and `isFalse:'0'` changes '0' to false and '1' to true
**/
const convertStringBooleansToBooleans = obj => {
    const booleanedObj = {}
    Object.keys(obj).forEach(key => {
        let field = obj[key]
        if (field === '0') {
            field = false
        } else if (field === '1' && key !== 'userRole') {
            field = true
        }
        booleanedObj[key] = field
    })
    return booleanedObj
}

const Loading = props => {
    const { message } = props
    const classes = useStyles()
    return (
        <>
            <div className={classes.loaderMessage}>
                <div>
                    {message}
                </div>
            </div>
            <Loader />
        </>
    )
}

let uppy
let getUppy = () => uppy

/** sessionStorage key holding the clientID a stale-token re-login was attempted for */
const RESYNC_STORAGE_KEY = 'auth:staleTokenResyncClientID'

//@TODO: refactor this to use redux, actions.sendRequest and pass contentGroup data in from redux store
export const requestContentGroups = (clientID, setContentGroups) => {
    xhr({
        method: 'post',
        body: JSON.stringify({
            clientID
        }),
        uri: `${config.uris.crud}Clientcontentgroups/getGroups`,
        headers: {
            'Authorization': config.apiAuthentication.vendorName,
            'Wsc-api-key': config.apiAuthentication.vendorKey,
            'Content-Type': 'application/json'
        }
    }, (err, resp) => {
        if (resp.statusCode === 200) {
            let contentGroups
            try {
                contentGroups = JSON.parse(resp.body)
            } catch (err) {
                console.log('json parse error:', err)
            }
            setContentGroups(contentGroups.data)
        } else {
            // handle error creating new user
        }
    })
}

export const requestSecurityRoles = (clientID, setSecurityRoles) => {
    xhr({
        method: 'post',
        body: JSON.stringify({
            clientID
        }),
        uri: `${config.uris.crud}SecurityRoles/getSecurityRoles`,
        headers: {
            'Authorization': config.apiAuthentication.vendorName,
            'Wsc-api-key': config.apiAuthentication.vendorKey,
            'Content-Type': 'application/json'
        }
    }, (err, resp) => {
        if (resp.statusCode === 200) {
            let securityRoles
            try {
                securityRoles = JSON.parse(resp.body)
            } catch (err) {
                console.log('json parse error:', err)
            }
            setSecurityRoles(securityRoles.data.roles)
        } else {
            // handle error creating new user
        }
    })
}

const Wrapper = props => {
    const { type, setUser, setClient, setSecurityRoles, userRolesPending, setContentGroups, userRole } = props
    const [userAppDataLoaded, setUserAppDataLoaded] = useState(false)
    const [appUserData, setAppUserData] = useState()
    const [appClientData, setAppClientData] = useState()
    const { loading, isAuthenticated, loginWithRedirect, logout, user } = useAuth0()
    const resolvedClientID = props.clientID !== undefined ? props.clientID : getUserClientID(user)
    if (!uppy && resolvedClientID !== undefined) {
        uppy = createUppy({clientID: resolvedClientID})
    }

    const handleSetup = data => {
        const userData = convertStringBooleansToBooleans(data.user)
        const clientData = convertStringBooleansToBooleans(data.clientInfo)
        setAppUserData({...userData})
        setAppClientData({...data.clientInfo})
        setUserAppDataLoaded(true)
        setUser(userData)
        setClient(clientData)
    }

    const doLogin = async () => {
        // Return to the page that was requested (e.g. an event link opened from
        // Admin Portal after a client swap) rather than the site root.
        loginWithRedirect({ appState: { targetUrl: window.location.pathname + window.location.search } })
    }

    /**
     * True when a re-login was started to replace a stale token.
     *
     * A client swap in Admin Portal moves the user to another client in the
     * database and in Auth0, but the token cached here keeps the clientID
     * claim from the last login. getLoginInfo returns the user's clientId from
     * the database, so a mismatch means the token is stale: log in again to
     * pick up the new claim. The Auth0 session is still live, so this is a
     * redirect round-trip, not a password prompt. One attempt per target
     * client, so a claim that never updates cannot loop.
     */
    const resyncStaleToken = dbClientID => {
        const attempted = sessionStorage.getItem(RESYNC_STORAGE_KEY)
        if (attempted === dbClientID) {
            console.warn(`Token clientID still differs from the user's clientID (${dbClientID}) after re-login`)
            return false
        }
        sessionStorage.setItem(RESYNC_STORAGE_KEY, dbClientID)
        doLogin()
        return true
    }
    let clientID = props.clientID
    useEffect(() => {
        // Bail when the token carries no clientID -- every request below
        // keys off it, so proceeding would fire a batch of calls that can
        // only fail. The render path surfaces this instead.
        if (!loading && isAuthenticated && user && !userAppDataLoaded && resolvedClientID !== undefined) {
            // get client info to request
            clientID = resolvedClientID
            requestSecurityRoles(clientID, setSecurityRoles)
            requestContentGroups(clientID, setContentGroups)
            let info
            if (type === 'Auth0') {
                info = {userID: user.sub.substring(user.sub.indexOf('|') + 1)}
            } else {
                info = {ldapUserName: user.nickname}
            }
            // request client info
            xhr({
                method: 'post',
                body: JSON.stringify({
                    clientID,
                    ...info
                }),
                uri: 'https://api.v3.invintus.com/v2/Console/getLoginInfo',
                headers: {
                    'Authorization': config.apiAuthentication.vendorName,
                    'Wsc-api-key': config.apiAuthentication.vendorKey,
                    'Content-Type': 'application/json'
                }
            }, (err, resp, body) => {
                if (resp.statusCode === 200) {
                    // successful request
                    // check that a user was returned in resp.body.data
                    let body
                    try {
                        body = JSON.parse(resp.body)
                    } catch(err) {
                        console.log('json parse error:', err)
                    }
                    if (body.data && body.data.user) {
                        const dbClientID = body.data.user.clientId !== undefined
                            ? String(body.data.user.clientId)
                            : undefined
                        if (type === 'Auth0' && dbClientID !== undefined) {
                            if (dbClientID !== String(clientID)) {
                                if (resyncStaleToken(dbClientID)) return
                            } else {
                                sessionStorage.removeItem(RESYNC_STORAGE_KEY)
                            }
                        }
                        // a user exists with the given login info finish login
                        handleSetup(body.data)
                    } else if (
                            // check if the user hasn't been created yet
                            type === 'Enterprise' &&
                            !body.data &&
                            body.meta === 'No Ldap user by this name'
                        ) {
                            // create the new user on their first login through ldap
                            xhr({
                                method: 'post',
                                body: JSON.stringify({
                                    clientID,
                                    auth0Payload: {
                                        ...user
                                    }
                                }),
                                uri: 'https://crud.v3.invintusmedia.com/users/createLdapUserDirect',
                                headers: {
                                    'Authorization': config.apiAuthentication.vendorName,
                                    'Wsc-api-key': config.apiAuthentication.vendorKey,
                                    'Content-Type': 'application/json'
                                }
                            }, (err, resp) => {
                                // with newly created account finish login
                                if (resp.statusCode === 200) {
                                    let newUserBody
                                    try {
                                        newUserBody = JSON.parse(resp.body)
                                    } catch (err) {
                                        console.log('json parse error:', err)
                                    }
                                    handleSetup(newUserBody.data) 
                                } else {
                                    // handle error creating new user
                                }
                            })
    
                        }
                    } else {
                    // request error
                }
            })
        }
    }, [user, isAuthenticated, userAppDataLoaded, loading, resolvedClientID])

    useEffect(() => {
        if (!loading && !isAuthenticated) {
            doLogin()
        }
    }, [isAuthenticated, loading])

    if (loading) {
        return <Loading message="Loading" />
    }
    // console.log('user :', user) // user data from auth0
    // console.log('user data:', appUserData) // user data merged with our data
    // console.log('client data:', appClientData) // client data returned for given user from our data
    if (!isAuthenticated) {
        return <Loading message="Please wait while you are redirected to the login page" />
    }
    // Authenticated, but the token carries no clientID. Nothing below can
    // work, and the session that produced it is not one the user can fix
    // by waiting -- so say so and offer the one action that resolves it,
    // rather than spinning on "Loading" forever.
    //
    // Typical cause: a session from another origin. Auth0 caches in
    // localStorage per origin, but a login started on production and
    // finished on localhost (or vice versa) can leave a token issued by a
    // different Auth0 application, which carries no clientID claim.
    if (isAuthenticated && user && resolvedClientID === undefined) {
        return (
            <div className={classes.loaderMessage}>
                <div>
                    <h3>This login is not valid for this site</h3>
                    <p>
                        You are signed in as <strong>{user.email || user.name || 'this user'}</strong>,
                        but the session does not carry an Invintus client. This
                        usually means it was issued for a different Control Center
                        site — signing in on production and returning to a local or
                        preview build will do it.
                    </p>
                    <p>Signing out and back in on this site clears it.</p>
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => logout({ returnTo: window.location.origin })}
                    >
                        Sign out
                    </button>
                </div>
            </div>
        )
    }

    if (isAuthenticated && user && userAppDataLoaded && appClientData && appUserData && !userRolesPending) {
        return <App user={appUserData} client={appClientData} getUppy={getUppy}/>
    } else {
        return <Loading message="Loading" />
    }
}

const mapStateToProps = state => {
    const userRolesPending = state.data.pending
    const userRole = state.data.user && state.data.user.userRole
    return {userRolesPending, userRole}
}

const mapDispatchToProps = dispatch => ({
    setUser: user => dispatch(actions.user.setUser({user})),
    setClient: client => dispatch(actions.user.setClient({client})),
    setSecurityRoles: securityRoles => dispatch(actions.user.setSecurityRoles({securityRoles})),
    setContentGroups: contentGroups => dispatch(actions.user.setContentGroups({contentGroups})),
    // sendRequest: (url, ) dispatch(actions.sendRequest({
    //     type: 'USER',
    //     url: 
    // }))
})

export default connect(mapStateToProps, mapDispatchToProps)(Wrapper)
